import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "i18n-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

// card #2566 — org-level sponsor library management for Score Admin. The
// sponsor entity, its score-api routes (listSponsors/putSponsor/
// deleteSponsor) and its upload gate (apps/score-api/src/lib/
// scoreboard-assets.js) all already exist from card #1635; this page is a
// new consumer of those same three routes via the generated score-common
// client, not a new concept. Layout/behavior mirrors score-fe's remote-control
// SponsorsPage.jsx (card #1639, design §7.2) almost verbatim: list + dialog
// editor with two staged image slots (square 1:1, wide 10:2) plus one line of
// text, previewed before Save, a separate delete-confirmation dialog. The
// deliberate difference here is i18n-fe (static keys) instead of score-fe's
// plain literals (score-fe carries no i18n stack, see that file's own
// comment), matching every other scoreadmin-fe page.
//
// Org-level, not show-scoped: unlike JudgeManager/HorseManager this page does
// not read useActiveShow at all, and its data doesn't change when the active
// show switches.

const IMAGE_ACCEPT = "image/png,image/jpeg";
const IMAGE_ALLOWED_TYPES = ["image/png", "image/jpeg"];
// Mirrors score-api's lib/logo.js LOGO_MIN_LONG_EDGE — instant client-side
// feedback ahead of the server's real assertMinDimension gate.
const IMAGE_MIN_LONG_EDGE = 600;
// Mirrors score-api's lib/scoreboard-assets.js ASPECT_TOLERANCE (+/-8%) and
// its two target ratios: square = 1:1, wide = 10:2 = 5:1.
const ASPECT_TOLERANCE = 0.08;
const ASPECT_TARGET = { square: 1, wide: 5 };

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("could not read file"));
    reader.readAsDataURL(file);
  });
}

function loadImageDimensions(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("could not read image"));
    img.src = dataUrl;
  });
}

function aspectLooksWrong(width, height, shape) {
  const target = ASPECT_TARGET[shape];
  const ratio = width / height;
  return Math.abs(ratio - target) / target > ASPECT_TOLERANCE;
}

// One image slot (square or wide) inside the sponsor editor. Purely local
// state: `value` is either an existing presigned URL (edit, untouched), a
// freshly-picked data URL (staged, not yet saved), or null (no image / user
// removed it). `onChange(nextValueOrNull)` bubbles the staged value up to the
// editor; the editor itself decides what to send on Save.
function SponsorImageSlot({ label, shape, value, onChange, disabled, testIdPrefix, t }) {
  const [error, setError] = useState(null);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file next time
    if (!file) return;

    setError(null);
    if (!IMAGE_ALLOWED_TYPES.includes(file.type)) {
      setError(t("sponsors.imageSlot.errorInvalidType"));
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const { width, height } = await loadImageDimensions(dataUrl);
      if (Math.max(width, height) < IMAGE_MIN_LONG_EDGE) {
        setError(t("sponsors.imageSlot.errorTooSmall", { minEdge: IMAGE_MIN_LONG_EDGE }));
        return;
      }
      if (aspectLooksWrong(width, height, shape)) {
        setError(
          shape === "wide"
            ? t("sponsors.imageSlot.errorWrongAspectWide")
            : t("sponsors.imageSlot.errorWrongAspectSquare")
        );
        return;
      }
      onChange(dataUrl);
    } catch {
      setError(t("sponsors.imageSlot.errorGeneric"));
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        {value ? (
          <Box
            component="img"
            src={value}
            alt=""
            data-testid={`${testIdPrefix}-preview`}
            sx={{
              maxHeight: 80,
              maxWidth: shape === "wide" ? 260 : 80,
              objectFit: "contain",
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              p: 0.5,
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" data-testid={`${testIdPrefix}-empty`}>
            {t("sponsors.imageSlot.noImage")}
          </Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            component="label"
            size="small"
            disabled={disabled}
            data-testid={`${testIdPrefix}-upload-button`}
          >
            {value ? t("sponsors.imageSlot.replace") : t("sponsors.imageSlot.upload")}
            <input
              type="file"
              accept={IMAGE_ACCEPT}
              hidden
              onChange={handleFileChange}
              data-testid={`${testIdPrefix}-file-input`}
            />
          </Button>
          {value ? (
            <Button
              variant="text"
              color="error"
              size="small"
              disabled={disabled}
              onClick={() => onChange(null)}
              data-testid={`${testIdPrefix}-remove-button`}
            >
              {t("sponsors.imageSlot.remove")}
            </Button>
          ) : null}
        </Stack>
      </Stack>
      {error ? (
        <Alert severity="error" sx={{ mt: 1 }} data-testid={`${testIdPrefix}-error`}>
          {error}
        </Alert>
      ) : null}
    </Box>
  );
}

const BLANK_SPONSOR = { id: null, name: "", textLine: "", squareUrl: null, wideUrl: null };

// Create (sponsor.id == null) and edit (sponsor.id set) share this dialog.
// `pending*` tracks whether the user staged a NEW image / an explicit
// removal for that slot this session; undefined means "untouched", so a Save
// on an edit that never touched an image omits that key entirely and the
// server (putSponsor) leaves it unchanged, per design §7.2.
function SponsorEditorDialog({ sponsor, onClose, onSaved, t }) {
  const scoreApi = useScoreApi();
  const isEdit = Boolean(sponsor?.id);

  const [name, setName] = useState(sponsor?.name ?? "");
  const [textLine, setTextLine] = useState(sponsor?.textLine ?? "");
  const [squarePreview, setSquarePreview] = useState(sponsor?.squareUrl ?? null);
  const [widePreview, setWidePreview] = useState(sponsor?.wideUrl ?? null);
  const [pendingSquare, setPendingSquare] = useState(undefined);
  const [pendingWide, setPendingWide] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSquareChange = (nextValueOrNull) => {
    setSquarePreview(nextValueOrNull);
    setPendingSquare(nextValueOrNull);
  };
  const handleWideChange = (nextValueOrNull) => {
    setWidePreview(nextValueOrNull);
    setPendingWide(nextValueOrNull);
  };

  const handleSave = async () => {
    setError(null);
    if (!name.trim()) {
      setError(t("sponsors.editor.nameRequired"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id: sponsor?.id ?? undefined,
        name: name.trim(),
        textLine: textLine.trim() ? textLine.trim() : null,
      };
      if (pendingSquare !== undefined) payload.squareImage = pendingSquare;
      if (pendingWide !== undefined) payload.wideImage = pendingWide;

      const saved = unwrap(await scoreApi.scoreboard.putSponsor(payload));
      onSaved(saved);
    } catch (err) {
      const code = err?.response?.data?.code;
      const message =
        code === "invalid_aspect_ratio"
          ? t("sponsors.editor.errorInvalidAspect")
          : code === "invalid_image_type"
            ? t("sponsors.editor.errorInvalidType")
            : code === "logo_too_small"
              ? t("sponsors.editor.errorTooSmall")
              : code === "logo_too_large"
                ? t("sponsors.editor.errorTooLarge")
                : code === "validation_error"
                  ? t("sponsors.editor.nameRequired")
                  : t("sponsors.editor.errorGeneric");
      setError(message);
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid="sponsor-editor-dialog"
    >
      <DialogTitle>{isEdit ? t("sponsors.editor.titleEdit") : t("sponsors.editor.titleNew")}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error ? (
            <Alert severity="error" data-testid="sponsor-editor-error">
              {error}
            </Alert>
          ) : null}
          <TextField
            label={t("sponsors.editor.nameLabel")}
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={saving}
            required
            fullWidth
            data-testid="sponsor-name-input"
          />
          <TextField
            label={t("sponsors.editor.textLineLabel")}
            value={textLine}
            onChange={(event) => setTextLine(event.target.value)}
            disabled={saving}
            fullWidth
            data-testid="sponsor-textline-input"
          />
          <SponsorImageSlot
            label={t("sponsors.editor.squareLabel")}
            shape="square"
            value={squarePreview}
            onChange={handleSquareChange}
            disabled={saving}
            testIdPrefix="sponsor-square"
            t={t}
          />
          <SponsorImageSlot
            label={t("sponsors.editor.wideLabel")}
            shape="wide"
            value={widePreview}
            onChange={handleWideChange}
            disabled={saving}
            testIdPrefix="sponsor-wide"
            t={t}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} data-testid="sponsor-cancel-button">
          {t("sponsors.editor.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          data-testid="sponsor-save-button"
        >
          {t("sponsors.editor.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteSponsorDialog({ sponsor, onClose, onDeleted, t }) {
  const scoreApi = useScoreApi();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      unwrap(await scoreApi.scoreboard.deleteSponsor(sponsor.id));
      onDeleted(sponsor.id);
    } catch {
      setError(t("sponsors.deleteDialog.error"));
      setDeleting(false);
    }
  };

  return (
    <Dialog open onClose={deleting ? undefined : onClose} data-testid="sponsor-delete-dialog">
      <DialogTitle>{t("sponsors.deleteDialog.title")}</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="sponsor-delete-error">
            {error}
          </Alert>
        ) : null}
        <Typography>{t("sponsors.deleteDialog.confirm", { name: sponsor.name })}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting} data-testid="sponsor-delete-cancel-button">
          {t("sponsors.deleteDialog.cancel")}
        </Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          data-testid="sponsor-delete-confirm-button"
        >
          {t("sponsors.deleteDialog.confirmButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Sponsors() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.sponsors"));
  const scoreApi = useScoreApi();
  const [sponsors, setSponsors] = useState(null); // null = still loading
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null); // null | BLANK_SPONSOR | a sponsor
  const [deleting, setDeleting] = useState(null); // null | a sponsor
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = unwrap(await scoreApi.scoreboard.listSponsors());
        if (!cancelled) setSponsors(items);
      } catch {
        if (!cancelled) setLoadError(t("sponsors.loadError"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scoreApi, t]);

  const handleSaved = (saved) => {
    setSponsors((prev) => {
      const list = prev ?? [];
      const index = list.findIndex((s) => s.id === saved.id);
      if (index === -1) return [...list, saved];
      const next = [...list];
      next[index] = saved;
      return next;
    });
    setEditing(null);
    setFeedback({ severity: "success", message: t("sponsors.savedMessage") });
  };

  const handleDeleted = (id) => {
    setSponsors((prev) => (prev ?? []).filter((s) => s.id !== id));
    setDeleting(null);
    setFeedback({ severity: "success", message: t("sponsors.deletedMessage") });
  };

  return (
    <Box sx={{ p: 3 }} data-testid="sponsors-page">
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom color="text.primary">
            {t("sponsors.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("sponsors.subtitle")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setEditing(BLANK_SPONSOR)}
          data-testid="sponsor-add-button"
        >
          {t("sponsors.addButton")}
        </Button>
      </Stack>

      {loadError ? (
        <Alert severity="error" data-testid="sponsors-load-error">
          {loadError}
        </Alert>
      ) : sponsors === null ? (
        <CircularProgress data-testid="sponsors-loading" />
      ) : sponsors.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="sponsors-empty">
          {t("sponsors.empty")}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table data-testid="sponsors-table">
            <TableHead>
              <TableRow>
                <TableCell>{t("sponsors.columns.name")}</TableCell>
                <TableCell>{t("sponsors.columns.textLine")}</TableCell>
                <TableCell>{t("sponsors.columns.square")}</TableCell>
                <TableCell>{t("sponsors.columns.wide")}</TableCell>
                <TableCell align="right">{t("sponsors.columns.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sponsors.map((sponsor) => (
                <TableRow key={sponsor.id} data-testid={`sponsor-row-${sponsor.id}`}>
                  <TableCell>{sponsor.name}</TableCell>
                  <TableCell>{sponsor.textLine || "-"}</TableCell>
                  <TableCell>
                    {sponsor.squareUrl ? (
                      <Box
                        component="img"
                        src={sponsor.squareUrl}
                        alt=""
                        sx={{ height: 40, width: 40, objectFit: "contain" }}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {sponsor.wideUrl ? (
                      <Box
                        component="img"
                        src={sponsor.wideUrl}
                        alt=""
                        sx={{ height: 40, width: 130, objectFit: "contain" }}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => setEditing(sponsor)}
                      title={t("sponsors.editButton")}
                      data-testid={`sponsor-edit-${sponsor.id}`}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => setDeleting(sponsor)}
                      title={t("sponsors.deleteButton")}
                      data-testid={`sponsor-delete-${sponsor.id}`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editing ? (
        <SponsorEditorDialog sponsor={editing} onClose={() => setEditing(null)} onSaved={handleSaved} t={t} />
      ) : null}

      {deleting ? (
        <DeleteSponsorDialog sponsor={deleting} onClose={() => setDeleting(null)} onDeleted={handleDeleted} t={t} />
      ) : null}

      <Snackbar
        open={feedback != null}
        autoHideDuration={feedback?.severity === "error" ? null : 4000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        {feedback ? (
          <Alert
            severity={feedback.severity}
            onClose={() => setFeedback(null)}
            data-testid={feedback.severity === "success" ? "sponsors-success" : "sponsors-error"}
            sx={{ width: "100%" }}
          >
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
